import { execFileSync } from 'node:child_process';
import { createHash } from 'node:crypto';
import { readFileSync } from 'node:fs';
import { resolve } from 'node:path';
import { type Environment, getCurrentEnvironment } from './env.ts';

const WEB_BASE_URLS: Record<Environment, string> = {
  dev: 'http://localhost:8080',
  'dev-shared': 'https://lore.dev.tanagram.ai',
  prod: 'https://lore.link',
};

type GetWebBaseURLOptions = {
  currentEnvironment?: Environment;
  discoverLocalWebUrl?: () => string | undefined;
  target?: string | undefined;
};

export function getWebBaseURL(options: GetWebBaseURLOptions = {}): string {
  const currentEnvironment = options.currentEnvironment ?? getCurrentEnvironment();
  const discoverLocalWebUrl = options.discoverLocalWebUrl ?? discoverLocalPortlessWebUrl;
  // Mirrors getApiBaseUrl: LORE_TARGET wins over the build-time env so
  // a single CLI build can talk to local, shared-dev, or prod without
  // rebuilding.
  const target = (options.target ?? process.env.LORE_TARGET)?.trim();
  if (target && Object.prototype.hasOwnProperty.call(WEB_BASE_URLS, target)) {
    if (target === 'dev') {
      return discoverLocalWebUrl() ?? WEB_BASE_URLS.dev;
    }
    return WEB_BASE_URLS[target as Environment];
  }
  if (currentEnvironment === 'dev') {
    const portlessUrl = discoverLocalWebUrl();
    if (portlessUrl) return portlessUrl;
  }
  return WEB_BASE_URLS[currentEnvironment];
}

function discoverLocalPortlessWebUrl(): string | undefined {
  if (process.env.LORE_DISABLE_PORTLESS_DISCOVERY === '1') return undefined;
  if (process.env.npm_lifecycle_event === 'test') return undefined;
  try {
    return execFileSync('portless', ['get', localPortlessWebName()], {
      encoding: 'utf8',
      stdio: ['ignore', 'pipe', 'ignore'],
    })
      .trim()
      .replace(/\/$/, '');
  } catch {
    return undefined;
  }
}

function localPortlessWebName(): string {
  // Mirror scripts/dev.mjs shouldCollapseWebHost: in a linked worktree the dev
  // supervisor registers the web host without a stack segment (a single label so
  // the WorkOS `*.lore.localhost` redirect allowlist matches), relying on the
  // Portless branch prefix for isolation. Resolve the same collapsed name here so
  // the CLI finds the running web host instead of falling back to the fixed dev
  // URL. The API host keeps its segment (see api.ts), so only web differs.
  if (shouldCollapseWebHost()) return 'lore';
  const stackSegment = discoverDevStackSegment();
  return stackSegment ? `${stackSegment}.lore` : 'lore';
}

function shouldCollapseWebHost(): boolean {
  // Match scripts/dev.mjs: collapse the web host only when Portless actually
  // prepends a branch subdomain — a linked worktree on a branch other than
  // main/master/detached HEAD. Otherwise `<segment>.lore.localhost` is already
  // single-label and the supervisor did not collapse it.
  if (!isLinkedWorktree(repoRoot())) return false;
  const branch = currentGitBranch();
  if (!branch || branch === 'HEAD' || branch === 'main' || branch === 'master') {
    return false;
  }
  return sanitizePortlessLabel(branch.split('/').pop() ?? '') !== '';
}

function currentGitBranch(): string {
  try {
    return execFileSync('git', ['rev-parse', '--abbrev-ref', 'HEAD'], {
      encoding: 'utf8',
      stdio: ['ignore', 'pipe', 'ignore'],
    }).trim();
  } catch {
    return '';
  }
}

function isLinkedWorktree(root: string): boolean {
  // Linked git worktrees have a `.git` *file* (a `gitdir:` pointer), not a dir;
  // readFileSync on the primary checkout's `.git` directory throws EISDIR.
  try {
    return readFileSync(resolve(root, '.git'), 'utf8').startsWith('gitdir:');
  } catch {
    return false;
  }
}

function discoverDevStackSegment(): string | undefined {
  const explicit = sanitizePortlessLabel(process.env.LORE_DEV_STACK_NAME ?? '');
  if (explicit) return explicit;
  const root = repoRoot();
  const checkout = sanitizePortlessLabel(root.split(/[\\/]/).pop() || 'lore');
  return [checkout, repoRootHash(root)]
    .filter(Boolean)
    .join('-')
    .slice(0, 63);
}

function repoRoot(): string {
  try {
    return execFileSync('git', ['rev-parse', '--show-toplevel'], {
      encoding: 'utf8',
      stdio: ['ignore', 'pipe', 'ignore'],
    }).trim();
  } catch {
    return resolve(process.cwd(), '../..');
  }
}

function repoRootHash(root: string): string {
  return createHash('sha1').update(root).digest('hex').slice(0, 8);
}

function sanitizePortlessLabel(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63);
}
